import { memo, type ReactNode, useCallback } from 'react';

import { useTrackActivatedIntercom, useTrackOpenIntercomChat } from '@air/analytics';
import { Help, Info, Megaphone, NavFeedback, SalesAgent, Support } from '@air/next-icons';
import { DropdownMenu, DropdownMenuItem, DropdownMenuSeparator } from '@air/primitive-dropdown-menu';
import { IconButton } from '@air/primitive-icon-button';
import { useAirModal } from '@air/provider-modal';

import { DemoFormModal } from '~/components/Modals/DemoFormModal';
import { useCannyUrl } from '~/components/v2/hooks/useCannyUrl';
import { Routes } from '~/constants/routes';
import { NEW_TAB } from '~/constants/urls';

export type HelpDropdownButtonProps = {
  additionalItems?: ReactNode;
};

export const HelpDropdownButton = memo(({ additionalItems }: HelpDropdownButtonProps) => {
  const [showDemoFormModal] = useAirModal(DemoFormModal);
  const { cannyUrl } = useCannyUrl();
  const { trackActivatedIntercom } = useTrackActivatedIntercom();
  const { trackOpenIntercomChat } = useTrackOpenIntercomChat();

  const onCannySelect = useCallback(() => {
    if (cannyUrl) {
      window.open(cannyUrl, NEW_TAB);
    }
  }, [cannyUrl]);

  const onChatWithSupportSelect = useCallback(() => {
    trackActivatedIntercom({ location: 'workspace-sidebar' });
    trackOpenIntercomChat();
  }, [trackActivatedIntercom, trackOpenIntercomChat]);

  const onHelpCenterSelect = useCallback(() => {
    window.open(Routes.helpCenter, NEW_TAB);
  }, []);

  const onMeetWithSalesSelect = useCallback(() => {
    showDemoFormModal();
  }, [showDemoFormModal]);

  const onNewAtAirSelect = useCallback(() => {
    window.open(Routes.changelog, NEW_TAB);
  }, []);

  return (
    <DropdownMenu
      align="end"
      alignOffset={4}
      className="w-[calc(var(--sidebar-width)-24px)]"
      side="top"
      sideOffset={4}
      trigger={<IconButton appearance="outline" color="grey" icon={Help} label="Help" size="small" />}
    >
      <DropdownMenuItem label="Help Center" onSelect={onHelpCenterSelect} prefix={<Info className="size-4" />} />
      <DropdownMenuItem
        label="Chat with Support"
        onSelect={onChatWithSupportSelect}
        prefix={<Support className="size-4" />}
      />
      <DropdownMenuItem
        label="Meet with Sales"
        onSelect={onMeetWithSalesSelect}
        prefix={<SalesAgent className="size-4" />}
      />
      <DropdownMenuItem label="New at Air" onSelect={onNewAtAirSelect} prefix={<Megaphone className="size-4" />} />
      <DropdownMenuItem
        disabled={!cannyUrl}
        label="Leave feedback"
        onSelect={onCannySelect}
        prefix={<NavFeedback className="size-4" />}
      />
      {additionalItems ? (
        <>
          <DropdownMenuSeparator />
          {additionalItems}
        </>
      ) : null}
    </DropdownMenu>
  );
});

HelpDropdownButton.displayName = 'HelpDropdownButton';
